import { useEffect, useMemo } from "react";
import Head from "next/head";

import useSWR from "swr";

import useLocalStorage from "hooks/useLocalStorage";
import { hasStoredStopId } from "@catchy/app-core";

import Nav from "@/components/Nav";
import { Alert } from "@catchy/ui";
import StopRow from "@/components/StopRow";
import { getCanonicalSiteUrl } from "@/lib/regions/client";

const fetcher = (url) => fetch(url).then((res) => res.json());

const stops = [
  { stop_id: "KELB", stop_name: "Kelburn" },
  { stop_id: "SALA", stop_name: "Salamanca" },
  { stop_id: "TALA", stop_name: "Talavera" },
  { stop_id: "CLIF", stop_name: "Clifton" },
  { stop_id: "LAMB", stop_name: "Lambton Quay" },
];

export async function getStaticProps() {
  let alerts = [];
  try {
    const resAlerts = await fetch(
      "https://catchy.nz/api/servicealerts/route-id/9",
    );

    if (resAlerts.status === 200) {
      alerts = await resAlerts.json();
    } else {
      console.error("Failed to fetch service alerts");
    }
  } catch (error) {
    console.error("Error fetching service alerts:", error);
  }

  let cruiseShips = [];
  try {
    const resCruiseShips = await fetch("https://catchy.nz/api/cruiseships");

    if (resCruiseShips.status === 200) {
      cruiseShips = await resCruiseShips.json();
    } else {
      console.error("Failed to fetch cruise ships data");
    }
  } catch (error) {
    console.error("Error fetching cruise ships data:", error);
  }

  return { props: { alerts, cruiseShips }, revalidate: 300 };
}

export default function CableCar({ preferredStopNames, alerts, cruiseShips }) {
  const siteUrl = getCanonicalSiteUrl();
  const { data: service_alerts } = useSWR(`/api/servicealerts/route-id/9`, {
    fetcher: fetcher,
    refreshInterval: 600000, // 10 minutes
    revalidateOnFocus: false,
    fallbackData: alerts,
  });

  const preferredStopNamesSSR = useMemo(
    () =>
      stops.map((stop) => {
        const preferredName = preferredStopNames.find((preferredStopName) =>
          hasStoredStopId(preferredStopName, stop.stop_id),
        )?.name;
        return { preferred_name: preferredName, ...stop };
      }),
    [preferredStopNames],
  );

  const today = new Date()
    .toLocaleString("en-AU", {
      timeZone: "Pacific/Auckland",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/")
    .reverse()
    .join("-");

  const shipsInTown = useMemo(
    () =>
      cruiseShips.filter(
        (ship) =>
          ship.arriveBerthDate <= today && ship.departBerthDate >= today,
        // (ship) => ship,
      ),
    [cruiseShips, today],
  );

  const [lastMain, setLastMain] = useLocalStorage("last_main", null);

  useEffect(() => {
    setLastMain("cable-car");
  }, [setLastMain]);

  const title = "Wellington Cable Car departures and timetable — Catchy";
  const description =
    "Find Wellington Cable Car departures, stop times, service updates, and timetable details.";
  const canonicalUrl = `${siteUrl}/cable-car`;

  return (
    <>
      <Head>
        <title key="title">{title}</title>

        <meta name="twitter:text:title" content={title} key="twitterTitle" />
        <meta
          name="twitter:text:description"
          content={description}
          key="twitterDescription"
        />

        <meta property="og:title" content={title} key="ogTitle" />
        <meta
          property="og:description"
          content={description}
          key="ogDescription"
        />
        <meta name="description" content={description} key="description" />
        <meta
          property="og:image"
          content="https://catchy.nz/share/share-cable-car.png"
          key="ogImage"
        />
        <meta property="og:url" content={canonicalUrl} key="ogUrl" />
        <link rel="canonical" href={canonicalUrl} key="canonicalUrl" />
      </Head>
      <Nav current="cablecar" />
      <div className="mb-20 flex flex-col pt-8">
        <h1 className="mb-3 px-[20px] text-3xl font-semibold">
          Cable Car Stops
        </h1>
        {preferredStopNamesSSR.map((element, key) => {
          return (
            <StopRow
              stop_name={
                element.preferred_name
                  ? element.preferred_name
                  : element.stop_name
              }
              stop_id={element.stop_id}
              type="cable_car"
              key={key}
            />
          );
        })}

        {shipsInTown.length > 0 && (
          <div className="py-4">
            <div className="relative mx-[20px] flex flex-row rounded-lg bg-blue-50 p-4 ring-2 ring-inset ring-blue-300 dark:bg-blue-800 dark:ring-blue-900">
              <div className="flex w-full flex-col gap-y-2">
                <h2 className="mb-1 text-pretty text-lg font-bold leading-5">
                  Cruise ship{shipsInTown.length > 1 && "s"} visiting Wellington{" "}
                  {new Intl.DateTimeFormat("en-AU", {
                    timeZone: "Pacific/Auckland",
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  }).format(new Date())}
                </h2>

                <ul className="list-inside list-disc pl-1 leading-tight">
                  {shipsInTown.map((ship, index) => (
                    <li key={index}>
                      <span className="font-semibold">{ship.vesselName}</span>
                      {ship.rounded_average_capacity
                        ? `: around ${new Intl.NumberFormat("en-AU").format(ship.rounded_average_capacity)} passengers`
                        : ""}
                    </li>
                  ))}
                </ul>
                <p className="leading-tight">
                  Expect the cable car to be busier than usual with an increase
                  in tourists.
                </p>
              </div>
            </div>
          </div>
        )}

        {service_alerts?.length > 0 && (
          <div className="flex flex-col gap-y-3 py-4 sm:gap-y-4">
            {service_alerts.map((e, k) => (
              <Alert
                effect={e.effect}
                severity_level={e.severity_level}
                header_text={e.header_text}
                description_text={e.description_text}
                url={e.url}
                key={k}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
